/**
 * Character creation agent.
 *
 * Generates setting-appropriate backgrounds and assigns GURPS stats
 * from a player's character concept. Uses "flash" tier for speed.
 */

import type { StreamChunk, ProviderConfig, TaskType } from "../types";
import { BaseAgent } from "./base";
import { dispatchStream } from "./providers";
import {
  backgroundGenerationPrompt,
  characterAssignmentPrompt,
} from "../prompts/characterCreation";
import { validateLlmJson, isBackgroundsArray, isCharacterAssignment } from "../schemaValidation";
import { BACKGROUNDS_SCHEMA, CHARACTER_ASSIGNMENT_SCHEMA } from "../schemas";

/** A single background option. */
export interface BackgroundOption {
  name: string;
  description: string;
}

/** LLM-generated character assignment. */
export interface CharacterAssignment {
  name: string;
  st: number;
  dx: number;
  iq: number;
  ht: number;
  skills: Record<string, number>;
  rationale: string;
}

/** Character agent: generates backgrounds and assigns GURPS stats. */
export class CharacterAgent extends BaseAgent {
  readonly taskType: TaskType = "world"; // reuses world task type for routing

  constructor(config: ProviderConfig) {
    super(config, 2048, 0.6);
  }

  async *stream(prompt: string, context?: string): AsyncGenerator<StreamChunk, void, unknown> {
    const userPrompt = this.buildPrompt(prompt, context);

    yield* dispatchStream({
      config: this.config,
      systemPrompt: "You are a character creation assistant. Return only valid JSON.",
      userPrompt,
      maxTokens: this.maxTokens,
      temperature: this.temperature,
      tier: "flash",
      modelId: this.currentModelId,
      responseSchema: this.jsonSchema,
    });
  }

  /** Generate 5 setting-appropriate backgrounds. */
  async generateBackgrounds(worldSetting: string): Promise<BackgroundOption[]> {
    this.jsonSchema = BACKGROUNDS_SCHEMA as unknown as Record<string, unknown>;
    try {
      const prompt = backgroundGenerationPrompt(worldSetting);
      const raw = await this.generateWithRouter(prompt, "background-generation");
      const result = await validateLlmJson(raw, isBackgroundsArray);
      return result.data as BackgroundOption[];
    } catch (err) {
      console.warn("[CharacterAgent] Backgrounds generation failed:", err);
    } finally {
      this.jsonSchema = undefined;
    }
    return [
      {
        name: "Wandering Stranger",
        description:
          "You arrived with nothing but the clothes on your back and a vague sense of purpose.",
      },
      {
        name: "Disgraced Soldier",
        description:
          "You once served in a forgotten war. Your body remembers the training even if your mind tries to forget.",
      },
      {
        name: "Hedge Scholar",
        description:
          "You traded comfort for knowledge, reading forbidden texts by candlelight in damp cellars.",
      },
      {
        name: "Street Survivor",
        description:
          "You grew up in the gutters, learning to read people and pick pockets before you could read words.",
      },
      {
        name: "Cursed Pilgrim",
        description: "You walk a path of atonement, though you can no longer remember the sin.",
      },
    ];
  }

  /** Generate a GURPS character from a concept. */
  async generateCharacter(
    worldSetting: string,
    background: string,
    archetype: string,
  ): Promise<CharacterAssignment> {
    this.jsonSchema = CHARACTER_ASSIGNMENT_SCHEMA as unknown as Record<string, unknown>;
    try {
      const prompt = characterAssignmentPrompt(worldSetting, background, archetype);
      const raw = await this.generateWithRouter(prompt, "character-assignment");
      const result = await validateLlmJson(raw, isCharacterAssignment);
      const parsed = result.data as CharacterAssignment;
      // Validate bounds
      const total = parsed.st + parsed.dx + parsed.iq + parsed.ht;
      if (
        total >= 28 &&
        total <= 56 &&
        parsed.st >= 1 &&
        parsed.st <= 20 &&
        parsed.dx >= 1 &&
        parsed.dx <= 20 &&
        parsed.iq >= 1 &&
        parsed.iq <= 20 &&
        parsed.ht >= 1 &&
        parsed.ht <= 20
      ) {
        return parsed;
      }
      console.warn("[CharacterAgent] Character: stats out of bounds or invalid", parsed);
    } catch (err) {
      console.warn("[CharacterAgent] Character generation failed:", err);
    } finally {
      this.jsonSchema = undefined;
    }
    return {
      name: "The Nameless",
      st: 10,
      dx: 10,
      iq: 10,
      ht: 10,
      skills: { Sword: 2, Survival: 2, Observation: 2 },
      rationale: "A balanced character with no particular strengths — the world will shape you.",
    };
  }
}
